import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Button, Col, Empty, Row, Spin, message } from 'antd';

import { PlusOutlined } from '@ant-design/icons';

import { api } from '../../http/API';
import { monitorAPI } from '../../http/MonitorAPI';
import { Field } from '../../models/Field';
import { FieldCropHistory } from '../../models/FieldCropHistory';
import { FieldSummary } from '../../models/FieldsSummary';

import FieldCard from './components/FieldCard';
import FieldSeasonModal from './components/FieldSeasonModal';
import FieldImportModal from './components/FieldImportModal';
import FieldForm from './components/FieldForm';

const FieldsPage = () => {
    const navigate = useNavigate();

    const [fields, setFields] = useState<FieldSummary[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [formOpen, setFormOpen] = useState<boolean>(false);
    const [editing, setEditing] = useState<Field | null>(null);
    const [seasonModalField, setSeasonModalField] = useState<Field | null>(null);
    const [seasonModalData, setSeasonModalData] = useState<FieldCropHistory | null>(null);
    const [importOpen, setImportOpen] = useState<boolean>(false);

    const loadAll = () => {
        setLoading(true);
        monitorAPI
            .GetFieldsSummary()
            .then((data) => setFields(data || []))
            .catch(() => message.error('Не удалось загрузить поля'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadAll();
    }, []);

    /** FieldSummary → Field для модалок редактирования/сезона */
    const toField = (fs: FieldSummary): Field => {
        const f = new Field();
        f.id = fs.id;
        f.name = fs.name;
        f.geo_json = fs.geo_json;
        return f;
    };

    const handleCreate = () => {
        setEditing(null);
        setFormOpen(true);
    };

    const handleEdit = (fs: FieldSummary) => {
        setEditing(toField(fs));
        setFormOpen(true);
    };

    const handleDelete = (id: number) => {
        api.DeleteField(id)
            .then(() => {
                message.success('Поле удалено');
                loadAll();
            })
            .catch(() => message.error('Не удалось удалить поле'));
    };

    const handleSubmit = (data: Partial<Field>) => {
        const promise = editing && editing.id ? api.UpdateField(editing.id, data) : api.CreateField(data);
        promise
            .then(() => {
                message.success(editing && editing.id ? 'Поле обновлено' : 'Поле создано');
                setFormOpen(false);
                loadAll();
            })
            .catch(() => message.error('Не удалось сохранить поле'));
    };

    const openSeason = (fs: FieldSummary) => {
        const field = toField(fs);
        setSeasonModalField(field);
        // Загружаем сезоны для этого поля
        api.GetFieldSeasons(fs.id)
            .then((rows) => setSeasonModalData(rows && rows.length > 0 ? rows[0] : null))
            .catch(() => setSeasonModalData(null));
    };

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setImportOpen(true)}>
                    Добавить поле
                </Button>
                <Button onClick={() => navigate('/settings/seasons')}>
                    Управление сезонами
                </Button>
            </div>

            {loading ? (
                <Spin />
            ) : fields.length === 0 ? (
                <div className="empty-box">
                    <h3>Нет полей</h3>
                    <p>Добавьте поле, чтобы видеть влагу, осадки и фазы культур.</p>
                    <Button type="primary" onClick={handleCreate}>
                        Добавить поле
                    </Button>
                </div>
            ) : (
                <Row gutter={[16, 16]}>
                    {fields.map((field) => (
                        <Col key={field.id} xs={24} sm={12} lg={8} xl={6}>
                            <FieldCard
                                field={field}
                                onOpen={() => navigate(`/fields/${field.id}`)}
                                onEdit={() => handleEdit(field)}
                                onDelete={() => handleDelete(field.id)}
                                onOpenSeason={() => openSeason(field)}
                            />
                        </Col>
                    ))}
                </Row>
            )}

            <FieldForm
                open={formOpen}
                field={editing}
                onCancel={() => setFormOpen(false)}
                onSubmit={handleSubmit}
            />
            <FieldImportModal
                open={importOpen}
                onCancel={() => setImportOpen(false)}
                onSaved={() => {
                    setImportOpen(false);
                    loadAll();
                }}
            />
            {seasonModalField && seasonModalField.id && (
                <FieldSeasonModal
                    open={!!seasonModalField}
                    fieldId={seasonModalField.id}
                    season={seasonModalData}
                    onCancel={() => {
                        setSeasonModalField(null);
                        setSeasonModalData(null);
                    }}
                    onSaved={() => {
                        setSeasonModalField(null);
                        setSeasonModalData(null);
                        loadAll();
                    }}
                />
            )}
        </div>
    );
};

export default FieldsPage;
